// ============================================================
// frontend/src/views/GuideView.tsx
// ============================================================
// In-app onboarding guide for a therapist using Consilium for the
// first time. Explains the day-to-day workflow step by step, with an
// animated lifecycle diagram and one fully worked example.
//
// This is a static, read-only view — no API calls. It's wired into the
// `guide` ViewState and reachable from the help button in the header
// and the "New here?" banner on the dashboard.
// ============================================================

import {
  Users, Layers, Calendar, NotebookPen, Wallet,
  ArrowRight, Sparkles,
} from 'lucide-react';
import type { ViewState } from '../App';

interface GuideViewProps {
  setView: (v: ViewState) => void;
}

export function GuideView({ setView }: GuideViewProps) {
  return (
    <div className="fade-in" style={{ maxWidth: 760, margin: '0 auto' }}>
      {/* ---- Header ---- */}
      <div style={{ marginBottom: 28 }}>
        <div className="mono-cap">Getting started</div>
        <h1 className="page-title">How Consilium works</h1>
        <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.6, marginTop: 8 }}>
          Consilium follows the natural rhythm of your practice: you keep a list of{' '}
          <strong>clients</strong>, give each one a <strong>package</strong> of sessions,
          run those <strong>sessions</strong> (writing notes as you go), and record{' '}
          <strong>payments</strong> against the package. The five steps below mirror
          that flow.
        </p>
      </div>

      {/* ---- Animated lifecycle diagram ---- */}
      <LifecycleDiagram />

      {/* ---- Step-by-step ---- */}
      <div className="guide-steps">
        <Step
          n={1}
          icon={Users}
          title="Add a client"
          tab="Clients"
          onGo={() => setView({ name: 'clients' })}
        >
          Open the <strong>Clients</strong> tab and press <em>“+ New client”</em>.
          Enter the name (the only required field) plus any phone, email, presenting
          concern, and background you have. You can edit all of this later from the
          client's page.
        </Step>

        <Step
          n={2}
          icon={Layers}
          title="Create reusable packages (optional, once)"
          tab="Packages"
          onGo={() => setView({ name: 'templates' })}
        >
          In the <strong>Packages</strong> tab you define <em>templates</em> — e.g.
          “8-Session CBT · 40,000 PKR”. A template is just a reusable starting point.
          You only set these up once; assigning one to a client <strong>copies</strong>{' '}
          its values, so editing the template later never changes packages already
          assigned.
        </Step>

        <Step
          n={3}
          icon={Calendar}
          title="Assign a package & schedule sessions"
        >
          On a client's page press <em>“Assign package”</em>. A 3-step popup walks you
          through it:
          <ol className="guide-sublist">
            <li><strong>Pick</strong> a template (or start from scratch).</li>
            <li><strong>Adjust</strong> the name, session count, fee, and start date for this client.</li>
            <li>
              <strong>Schedule</strong> — choose <em>Skip for now</em>,{' '}
              <em>Schedule first only</em>, or <em>Schedule all sessions</em> at a
              regular interval (weekly, bi-weekly, or custom).
            </li>
          </ol>
          For a single appointment with no package, use <em>“One-off session”</em> instead —
          Consilium quietly creates a 1-session package behind the scenes.
          <p className="guide-note">
            A client can have only <strong>one active package at a time</strong>.
            Finish or cancel the current one before assigning the next.
          </p>
        </Step>

        <Step
          n={4}
          icon={NotebookPen}
          title="Run the session & write notes"
          tab="Sessions"
          onGo={() => setView({ name: 'sessions' })}
        >
          The <strong>Today</strong> dashboard and <strong>Sessions</strong> tab list
          what's coming up. Open a session to set its agenda, write notes, attach links
          or materials, and jot the <em>next session's</em> agenda. Notes{' '}
          <strong>auto-save</strong> as you type — there's no save button to forget.
          When you're done, mark the session <em>Completed</em> (or{' '}
          <em>Cancelled</em> / <em>No-show</em>).
        </Step>

        <Step
          n={5}
          icon={Wallet}
          title="Record payments"
        >
          Back on the client's page, each package shows how much has been paid. Add a
          payment whenever money comes in — full or partial. The{' '}
          <strong>Today</strong> dashboard surfaces packages with an outstanding
          balance under <em>“Pending”</em>, so nothing slips through.
        </Step>
      </div>

      {/* ---- Worked example ---- */}
      <WorkedExample />

      {/* ---- CTA ---- */}
      <div style={{ textAlign: 'center', marginTop: 32 }}>
        <button className="btn-primary" onClick={() => setView({ name: 'clients' })}>
          Add your first client <ArrowRight size={16} style={{ marginLeft: 6 }} />
        </button>
      </div>
    </div>
  );
}

// ============================================================
// LifecycleDiagram — the five-stage flow with an animated connector
// ============================================================
function LifecycleDiagram() {
  const stages = [
    { icon: Users, label: 'Client' },
    { icon: Layers, label: 'Package' },
    { icon: Calendar, label: 'Sessions' },
    { icon: NotebookPen, label: 'Notes' },
    { icon: Wallet, label: 'Payment' },
  ];

  return (
    <div className="guide-flow card" aria-hidden="true">
      {stages.map((s, i) => {
        const Icon = s.icon;
        return (
          <div className="guide-flow-stage" key={s.label}>
            <div className="guide-flow-node" style={{ animationDelay: `${i * 0.25}s` }}>
              <Icon size={20} />
            </div>
            <div className="guide-flow-label">{s.label}</div>
            {i < stages.length - 1 && (
              <div className="guide-flow-arrow">
                <svg width="100%" height="2" viewBox="0 0 100 2" preserveAspectRatio="none">
                  <line x1="0" y1="1" x2="100" y2="1" className="guide-flow-line" />
                </svg>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// Step — one numbered card, optionally with a "go to tab" jump
// ============================================================
interface StepProps {
  n: number;
  icon: typeof Users;
  title: string;
  tab?: string;
  onGo?: () => void;
  children: React.ReactNode;
}

function Step({ n, icon: Icon, title, tab, onGo, children }: StepProps) {
  return (
    <div className="card guide-step">
      <div className="guide-step-num">{n}</div>
      <div style={{ flex: 1 }}>
        <div className="guide-step-head">
          <Icon size={17} color="var(--accent)" />
          <h2 className="guide-step-title">{title}</h2>
          {tab && onGo && (
            <button className="btn-ghost guide-step-jump" onClick={onGo}>
              Open {tab} <ArrowRight size={13} style={{ marginLeft: 4 }} />
            </button>
          )}
        </div>
        <div className="guide-step-body">{children}</div>
      </div>
    </div>
  );
}

// ============================================================
// WorkedExample — one concrete end-to-end story
// ============================================================
function WorkedExample() {
  const rows = [
    ['Add client', 'Create “Ayesha Khan”, presenting: work-related anxiety.'],
    ['Assign package', 'On Ayesha’s page → Assign package → pick “8-Session CBT”.'],
    ['Adjust & schedule', 'Keep 8 sessions / 40,000 PKR. Choose “Schedule all”, first session Mon 3:00pm, weekly.'],
    ['Run session 1', 'Open it from Today, write notes, set next agenda, mark Completed.'],
    ['Record payment', 'Ayesha pays 20,000 PKR up front → add a partial payment; package shows 20,000 / 40,000.'],
    ['Following weeks', 'Each Monday’s session is already on the calendar — open, note, complete, repeat.'],
  ];

  return (
    <div className="card" style={{ marginTop: 28, padding: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Sparkles size={16} color="var(--accent)" />
        <h2 className="guide-step-title" style={{ margin: 0 }}>A complete example</h2>
      </div>
      <div className="guide-example">
        {rows.map(([label, detail], i) => (
          <div className="guide-example-row" key={i}>
            <div className="guide-example-step">{i + 1}</div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
              <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 2, lineHeight: 1.5 }}>{detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
